import bitsByWordsCount from "../static/bitsByWordCount.json";
import { SHA256 } from "crypto-js";
import { ethers } from "ethers";
import {
  Card,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  Typography,
  Alert,
  Box,
  Chip,
  Divider,
} from "@mui/material";

export default function Status({ wordsCount, diceNumbers, passPhrase }) {
  function getBitString() {
    return diceNumbers
      .map((e) => Number(e).toString(2))
      .join("")
      .slice(0, bitsByWordsCount[wordsCount]);
  }
  function calculateChecksum() {
    const bits = getBitString();

    const bytes = [];
    for (let i = 0; i < bits.length; i += 8) {
      const byte = bits.slice(i, i + 8);
      bytes.push(parseInt(byte, 2));
    }

    const entropy = new Uint8Array(bytes);

    const hash = SHA256(entropy);
    const checksumLength = (entropy.length * 8) / 32;
    const checksum = parseInt(hash.toString(), 16)
      .toString(2)
      .padStart(256, "0")
      .slice(0, checksumLength);

    return checksum;
  }

  function finalBitsToWords() {
    const finalBits = getBitString();

    if (bitsByWordsCount[wordsCount] > finalBits.length) {
      return {
        success: false,
        mnemonic: "Not enough entropy. Please roll the dice more.",
      };
    }

    const bytes = [];
    for (let i = 0; i < finalBits.length; i += 8) {
      const byte = finalBits.slice(i, i + 8);
      bytes.push(parseInt(byte, 2));
    }

    const mnemonic = ethers.Mnemonic.fromEntropy(
      new Uint8Array(bytes),
      passPhrase
    );
    return { success: true, mnemonic: mnemonic.phrase };
  }

  const { success, mnemonic } = finalBitsToWords();

  return (
    <Card>
      <CardHeader title="Current Status" />
      <List dense>
        <ListItem>
          <ListItemText
            primary="Total words"
            secondary={<strong>{wordsCount}</strong>}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Total rolls"
            secondary={<strong>{diceNumbers.length}</strong>}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Last 10 dice rolls"
            secondary={
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {diceNumbers.slice(-10).map((num, i) => (
                  <Chip
                    key={i}
                    label={num}
                    size="small"
                    style={{ width: "25px" }}
                  />
                ))}
              </Box>
            }
          />
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText
            primary="Number Distribution"
            secondary={
              <Box>
                {Array.from({ length: 6 }, (_, i) => (
                  <div key={i}>
                    <span
                      className="me-2"
                      style={{
                        display: "inline-block",
                        minWidth: "25px",
                        marginBottom: "2px",
                      }}
                    >
                      <Chip
                        key={i}
                        label={i + 1}
                        size="small"
                        style={{ width: "25px" }}
                      />
                    </span>
                    <span
                      className="text-monospace"
                      style={{ marginLeft: "5px" }}
                    >
                      {"*".repeat(
                        diceNumbers.filter((num) => num === i + 1).length
                      )}
                    </span>
                  </div>
                ))}
              </Box>
            }
          />
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText
            primary="Generated Entropy"
            secondary={
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ wordBreak: "break-all", fontSize: "10px" }}
              >
                {diceNumbers.length === 0 ? "-" : getBitString()}
              </Typography>
            }
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Checksum"
            secondary={
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "10px" }}
              >
                {getBitString().length < bitsByWordsCount[wordsCount]
                  ? "-"
                  : calculateChecksum()}
              </Typography>
            }
          />
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText
            primary="Mnemonic Words"
            primaryTypographyProps={{ fontSize: "18px", fontWeight: "bold" }} // Title styling
            secondary={
              success ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {mnemonic.split(" ").map((word, index) => (
                    <Box key={index} sx={{ flexBasis: "calc(20% - 8px)" }}>
                      {" "}
                      {/* gap is 8px */}
                      <Typography variant="body2" sx={{ fontSize: "15px" }}>{`${
                        // Content styling
                        index + 1
                      }. ${word}`}</Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Alert severity="warning">{mnemonic}</Alert>
              )
            }
          />
        </ListItem>
      </List>
    </Card>
  );
}
